/**
 * The two guardrail fields — `docs/comparison-emrx.md` §5.3, a hard "do not port" boundary rather
 * than a style note.
 *
 * `emrx` derives both of the values typed here from demographics, and both derivations are
 * self-flagged by its own author as wrong:
 *
 *   - `fitzpatrickFor` (`emrx/src/mock/phototherapy.ts:95-103`) infers Fitzpatrick skin phototype
 *     from recorded **race**. Its own comment: "a production system must NOT do this — Fitzpatrick is
 *     a phototype assessed by how skin burns and tans, and it cuts across race in both directions."
 *     In a certified product this is clinically wrong and a discrimination exposure.
 *   - `categoryFor` (`emrx/src/mock/ipledge.ts:104-111`) infers iPLEDGE risk category from
 *     administrative gender and age. Its own comment: "neither gender nor age determines it — this is
 *     exactly the kind of inference a production system must not make." It is both a REMS compliance
 *     failure (the wrong category can withhold or waive required pregnancy testing) and it misgenders
 *     patients.
 *
 * **Neither `fitzpatrickFor` nor `categoryFor` is ported, in any form, anywhere in this module or
 * this codebase.** This module exists specifically to make the correct replacement structurally
 * available: `FitzpatrickSkinType` and `IpledgeRiskCategory` are real, closed value sets a clinician
 * records as an explicit answer — through `server/src/db/schema/dermatology.ts`'s
 * `dermatologyPatientProfiles.fitzpatrickSkinType`/`ipledgeRiskCategory`, written only by a route that
 * takes the value from a request body the same way every other clinical field in this codebase does,
 * never computed from `patients.administrativeSex`, `patients.genderIdentity`, or any race/ethnicity
 * field. `dermatologyPatientProfiles` also carries `fitzpatrickRecordedBy`/`fitzpatrickRecordedAt` and
 * the iPLEDGE equivalents specifically so "who answered this, and when" is itself part of the record,
 * not just the answer — a property no inferred value could ever carry.
 *
 * Every function in this module (and in `phototherapy.ts`/`ipledge.ts`, which consume these types)
 * takes the recorded value as an explicit argument. None accepts a demographic field as input, and
 * `guardrailNoInference.test.ts` (in this directory) asserts that structurally — by scanning this
 * module's own source, and every dermatology module's source, for the banned identifiers and function
 * names — rather than trusting a comment to stay true.
 */

/**
 * Fitzpatrick skin phototype, I through VI, stored as the numeral `emrx`'s own `NBUVB_PROTOCOL`
 * table is keyed by (`phototherapy.ts`) so a recorded type indexes the dosing table directly with no
 * translation step to get wrong.
 */
export const FITZPATRICK_SKIN_TYPES = ['1', '2', '3', '4', '5', '6'] as const;
export type FitzpatrickSkinType = (typeof FITZPATRICK_SKIN_TYPES)[number];

export function isFitzpatrickSkinType(value: string): value is FitzpatrickSkinType {
  return (FITZPATRICK_SKIN_TYPES as readonly string[]).includes(value);
}

/**
 * iPLEDGE risk category. Two values, matching the programme's own two-track structure
 * (`emrx/src/mock/terminology.ts`'s `IpledgeCategory`) — **the value itself is unchanged from
 * `emrx`'s naming; what changes is that it is never derived.** A real intake asks the clinical
 * reproductive-potential question directly (documented sterilization, menopause status, current
 * pregnancy-prevention counseling) rather than reading gender and age off the chart.
 */
export const IPLEDGE_RISK_CATEGORIES = ['can_become_pregnant', 'cannot_become_pregnant'] as const;
export type IpledgeRiskCategory = (typeof IPLEDGE_RISK_CATEGORIES)[number];

export function isIpledgeRiskCategory(value: string): value is IpledgeRiskCategory {
  return (IPLEDGE_RISK_CATEGORIES as readonly string[]).includes(value);
}
